import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import styles from "./accountDeletion.module.scss";
import {
  accountDeletionDefaults,
  accountDeletionSchema,
  AccountDeletionFormValues,
} from "./accountDeletion.schema";
import { submitAccountDeletionRequest } from "./accountDeletion.service";

export const AccountDeletionForm = () => {
  const [submitState, setSubmitState] = useState<
    | { kind: "idle" }
    | { kind: "submitting" }
    | { kind: "success" }
    | { kind: "error"; message: string }
  >({ kind: "idle" });

  const {
    control,
    handleSubmit,
    register,
    watch,
    formState: { errors },
  } = useForm<AccountDeletionFormValues>({
    resolver: yupResolver(accountDeletionSchema),
    defaultValues: accountDeletionDefaults,
    mode: "onSubmit",
  });

  const confirmChecked = watch("confirm");

  const onSubmit = handleSubmit(async (values) => {
    setSubmitState({ kind: "submitting" });
    try {
      await submitAccountDeletionRequest({
        fullName: values.fullName.trim(),
        email: values.email.trim(),
        accountType: values.accountType,
        reason: values.reason.trim() ? values.reason.trim() : undefined,
        confirm: true,
      });
      setSubmitState({ kind: "success" });
    } catch {
      setSubmitState({
        kind: "error",
        message:
          "Could not submit. Please try again or email dfoster@daigleclean.com.",
      });
    }
  });

  if (submitState.kind === "success") {
    return (
      <div className={styles.successPanel} role="status">
        <strong>Request received.</strong> We'll process it within 30 days.
        You'll hear from us at the email you provided if we need anything else.
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={onSubmit} noValidate>
      <div className={styles.field}>
        <label htmlFor="adr-fullName">Full name</label>
        <input
          id="adr-fullName"
          type="text"
          autoComplete="name"
          {...register("fullName")}
        />
        {errors.fullName && (
          <span className={styles.error}>{errors.fullName.message}</span>
        )}
      </div>

      <div className={styles.field}>
        <label htmlFor="adr-email">Registered email</label>
        <input
          id="adr-email"
          type="email"
          autoComplete="email"
          {...register("email")}
        />
        {errors.email && (
          <span className={styles.error}>{errors.email.message}</span>
        )}
      </div>

      <div className={styles.field}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>Account type</span>
        <Controller
          name="accountType"
          control={control}
          render={({ field }) => (
            <div className={styles.radioGroup}>
              <label className={styles.radio}>
                <input
                  type="radio"
                  value="customer"
                  checked={field.value === "customer"}
                  onChange={() => field.onChange("customer")}
                />
                Customer
              </label>
              <label className={styles.radio}>
                <input
                  type="radio"
                  value="employee"
                  checked={field.value === "employee"}
                  onChange={() => field.onChange("employee")}
                />
                Employee
              </label>
            </div>
          )}
        />
        {errors.accountType && (
          <span className={styles.error}>{errors.accountType.message}</span>
        )}
      </div>

      <div className={styles.field}>
        <label htmlFor="adr-reason">Reason for deletion (optional)</label>
        <textarea id="adr-reason" {...register("reason")} />
        {errors.reason && (
          <span className={styles.error}>{errors.reason.message}</span>
        )}
      </div>

      <label className={styles.checkboxRow}>
        <input type="checkbox" {...register("confirm")} />
        <span>
          I understand this will permanently delete my account and the data
          listed above.
        </span>
      </label>
      {errors.confirm && (
        <span className={styles.error}>{errors.confirm.message}</span>
      )}

      {submitState.kind === "error" && (
        <span className={styles.error} role="alert">
          {submitState.message}
        </span>
      )}

      <button
        type="submit"
        className={styles.submit}
        disabled={!confirmChecked || submitState.kind === "submitting"}
      >
        {submitState.kind === "submitting" ? "Submitting…" : "Submit deletion request"}
      </button>
    </form>
  );
};
